import { motion, useReducedMotion } from 'motion/react';
import korzaLogoWhite from '@/assets/korza_logo_white.png';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { KorzaTorus } from '@/components/brand/korza-torus/korza-torus';
import { useI18nContext } from '@/i18n/i18n-react';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';
import { AuthPushStack } from '@/pages/auth/auth-push-stack';

const PANEL_GRADIENT =
  'radial-gradient(55% 45% at 75% 18%, rgba(21,93,252,0.95), transparent 70%), radial-gradient(50% 40% at 15% 90%, rgba(120,160,255,0.5), transparent 70%), linear-gradient(165deg, #0B348C 0%, #15191E 78%)';

// The phone's wallpaper is the brand book's own (Apresentação IDV, the
// splash on a held iPhone) rather than Apple's default.
const WALLPAPER_GRADIENT =
  'radial-gradient(70% 40% at 50% 104%, rgba(150,185,255,0.85), transparent 70%), radial-gradient(80% 45% at 90% 18%, rgba(21,93,252,0.85), transparent 70%), linear-gradient(180deg, #0B1E4F 0%, #15191E 55%, #0B348C 100%)';

// Backdrop for the Login/Create Account right column: the brand book's
// grainy blue gradient, the live 3D torus spinning behind a phone, and that
// phone's lock screen receiving real Korza clients' pushes — the product
// (app + push) shown instead of described. The phone is the onboarding's
// IPhoneMockup, so it matches the real device like every other preview.
export function AuthBackdrop() {
  const { LL, locale } = useI18nContext();
  const shouldReduceMotion = useReducedMotion();
  const dateLabel = new Intl.DateTimeFormat(
    locale === 'pt' ? 'pt-BR' : 'en-US',
    { weekday: 'long', day: 'numeric', month: 'long' }
  ).format(new Date());

  return (
    <section
      aria-label={LL.auth.showcaseLabel()}
      className="relative flex size-full items-center justify-center overflow-hidden bg-[#15191E]"
    >
      <div
        aria-hidden="true"
        className="absolute -inset-[10%]"
        style={{ background: PANEL_GRADIENT }}
      />
      <GrainOverlay />
      <KorzaTorus zoom={0.8} className="absolute inset-0 size-full" />

      <motion.div
        aria-hidden="true"
        className="relative drop-shadow-[0_40px_60px_rgba(0,0,0,0.45)]"
        animate={shouldReduceMotion ? undefined : { y: [0, -10, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      >
        <IPhoneMockup sceneKey="auth-lock-screen" chromeBaked>
          <LockScreenScene
            dateLabel={dateLabel}
            wallpaper={
              <>
                <div
                  className="absolute inset-0"
                  style={{ background: WALLPAPER_GRADIENT }}
                />
                <GrainOverlay />
              </>
            }
          >
            <AuthPushStack />
          </LockScreenScene>
        </IPhoneMockup>
      </motion.div>

      <img
        src={korzaLogoWhite}
        alt="Korza"
        className="absolute bottom-6 right-6 h-5 object-contain opacity-85"
      />
    </section>
  );
}
